import Itly from 'itly';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { ButtonWrapper, Prompt } from './styles';
import { useChatContext } from '../ChatContext';
import { TemplatePageContext } from 'types/Navigation';

const PDP_PRODUCT_ASSISTANT_PROMPT = 'Have questions about this product? Ask me...';
const DEFAULT_PRODUCT_ASSISTANT_PROMPT = 'Need help finding the right product? Ask me a question...';

export const ChatButton = ({ ariaLabel, templatePageContext }: { ariaLabel: string; templatePageContext: TemplatePageContext }): JSX.Element => {
  const [isAboveStickyCTA, setIsAboveStickyCTA] = useState(false);
  const [toastOffset, setToastOffset] = useState(0);
  const [prompt, setPrompt] = useState<string | null>(null);
  
  const { isOpen, setIsOpen } = useChatContext();

  const hasOpenedAssistant = localStorage.getItem('hasOpenedAssistant') === 'true';

  const checkStickyCTA = useCallback(() => {
    const sticky = document.getElementById('sticky-cta-container');
    setIsAboveStickyCTA(!!sticky && sticky.classList.contains('show'));
  }, []);

  const checkToasts = useCallback((mutations: MutationRecord[]) => {
    const toastContainer = document.getElementsByClassName('Toastify__toast-container')[0] as HTMLElement;
    setToastOffset(toastContainer?.offsetHeight ?? 0);
  }, []);

  useEffect(() => {
    const toastifyElement = document.getElementsByClassName('Toastify')[0];   

    const observer = new MutationObserver(checkToasts);

    if (toastifyElement && toastifyElement instanceof Node) {
      const config = { childList: true, subtree: true };
      observer.observe(toastifyElement, config);
    }
    
    return () => {
      observer.disconnect();
    };
  }, [checkToasts]);

  useEffect(
    function checkStickyCTAOnScroll() {
      window.addEventListener('scroll', checkStickyCTA, { passive: true });
      return () => window.removeEventListener('scroll', checkStickyCTA);
    },
    [checkStickyCTA]
  );

  useEffect(
    function checkyStickyCTAOnLoad() {
      setTimeout(() => checkStickyCTA(), 50);
    },
    [checkStickyCTA, isOpen]
  );

  useEffect(function setPromptWithContext() {
    const productAssistantPrompt = templatePageContext === 'PDP' ? PDP_PRODUCT_ASSISTANT_PROMPT : DEFAULT_PRODUCT_ASSISTANT_PROMPT;
    const timeout = setTimeout(() => {
      setPrompt(productAssistantPrompt);
    }, 2000);
    return () => clearTimeout(timeout);
  }, [templatePageContext]);

  const promptComponent = useMemo(() => {
    return prompt ? <Prompt isAboveStickyCTA={isAboveStickyCTA} toastOffset={toastOffset}>{prompt}</Prompt> : null;
  }, [prompt, isAboveStickyCTA, toastOffset]);

  const handleClick = useCallback(() => {
    if (!isOpen) {
      Itly.productAssistantOpened({ current_url: window.location.href });
    }

    localStorage.setItem('hasOpenedAssistant', 'true');
    setIsOpen(!isOpen);
  }, [setIsOpen, isOpen]);

  return (
    <>
      {!isOpen && !hasOpenedAssistant && promptComponent}
      <ButtonWrapper isAboveStickyCTA={isAboveStickyCTA} toastOffset={toastOffset} onClick={handleClick} aria-label={ariaLabel}>
        {isOpen && (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M18.7929 6.71782C19.1834 6.3273 19.1834 5.69413 18.7929 5.30361L18.6964 5.20711C18.3059 4.81658 17.6727 4.81658 17.2822 5.20711L12 10.4893L6.71782 5.20711C6.3273 4.81658 5.69413 4.81658 5.30361 5.20711L5.20711 5.30361C4.81658 5.69413 4.81658 6.3273 5.20711 6.71782L10.4893 12L5.20711 17.2822C4.81658 17.6727 4.81658 18.3059 5.20711 18.6964L5.30361 18.7929C5.69413 19.1834 6.3273 19.1834 6.71782 18.7929L12 13.5107L17.2822 18.7929C17.6727 19.1834 18.3059 19.1834 18.6964 18.7929L18.7929 18.6964C19.1834 18.3059 19.1834 17.6727 18.7929 17.2822L13.5107 12L18.7929 6.71782Z"
              fill="#333333"
            />
          </svg>
        )}
        {!isOpen && (
          <>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M4.55375 20.5L3.5 19.4462L11.1308 11.8L6.75975 10.723L11.325 7.8885L10.929 2.5L15.048 5.977L20.025 3.95L18.023 8.94225L21.5 13.046L16.1115 12.675L13.2615 17.2402L12.175 12.8693L4.55375 20.5ZM5.14425 7.40375L3.5 5.7595L5.14425 4.1155L6.7885 5.7595L5.14425 7.40375ZM13.875 13.4348L15.3057 11.1038L18.0443 11.3077L16.275 9.20575L17.304 6.671L14.7693 7.7L12.6673 5.946L12.8712 8.66925L10.55 10.1155L13.2038 10.7712L13.875 13.4348ZM18.2405 20.5L16.5962 18.8557L18.2405 17.2115L19.8845 18.8557L18.2405 20.5Z"
                fill="#1F1F1F"
              />
            </svg>
            {!hasOpenedAssistant && <span style={{ position: 'absolute', top: 0, right: 0, transform: 'translate(10%, -10%)', width: '18px' }}>
              <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
                <circle cx="50" cy="50" r="45" fill="#C00" />
                <text x="50" y="65" font-family="Arial, sans-serif" font-size="50" font-weight="bold" text-anchor="middle" fill="white">1</text>
              </svg>
            </span>}
          </>
        )}
      </ButtonWrapper>
    </>
  );
};
